import { useCallback, useEffect, useRef } from 'react';

import type {
  DragEventHandler,
  MouseEventHandler,
  MutableRefObject,
} from 'react';

const defaultState = {
  isDragging: false,
  dragStart: null,
  dragStartPosition: 0,
};

export const useSliderDragging = <T extends HTMLElement>({
  sliderRef,
}: {
  sliderRef: MutableRefObject<T>;
}) => {
  // using a ref for the current dragging state is much more performant (no re-renders needed)
  const state = useRef<{
    isDragging: boolean;
    dragStart: number | null;
    dragStartPosition: number;
  }>(defaultState);

  function setState(updated: typeof state.current) {
    state.current = updated;
  }

  useEffect(() => {
    function onMouseLeave() {
      if (state.current.isDragging) {
        setState(defaultState);
      }
    }

    function onMouseMove(event: HTMLElementEventMap['mousemove']) {
      if (!state.current.isDragging) {
        return;
      }

      const offset = event.clientX - state.current.dragStartPosition;
      const scrollLeft = sliderRef.current.scrollLeft;

      setState({ ...state.current, dragStartPosition: event.clientX });

      sliderRef.current.scrollLeft = scrollLeft - offset;
    }

    function onMouseUp() {
      // this one is triggered before the click event on slider element, so we need
      // to keep the dragStart time to avoid all clicks from being prevented
      setState({
        ...defaultState,
        dragStart: state.current.dragStart,
      });
    }

    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    document.documentElement.addEventListener('mousemove', onMouseMove, {
      passive: true,
    });
    document.documentElement.addEventListener('mouseup', onMouseUp);

    return () => {
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      document.documentElement.removeEventListener('mousemove', onMouseMove);
      document.documentElement.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown: MouseEventHandler<T> = useCallback((event) => {
    // store the position and time we start at
    setState({
      isDragging: true,
      dragStart: Date.now(),
      dragStartPosition: event.clientX,
    });
  }, []);

  const handleClickCapture: MouseEventHandler<T> = useCallback((event) => {
    // we can treat the click event as the end of dragging
    // prevent the click only if it took more than 200ms
    if (Date.now() - (state.current.dragStart ?? 0) > 200) {
      event.preventDefault();
      event.stopPropagation();
    }

    setState(defaultState);
  }, []);

  const handleDragStartCapture: DragEventHandler<T> = useCallback((event) => {
    // prevent the default behavior of dragging links in Firefox
    if (state.current.isDragging) {
      event.preventDefault();
    }
  }, []);

  return { handleMouseDown, handleClickCapture, handleDragStartCapture };
};
